import React, { useState, useEffect } from 'react';
import {
  Database, Download, Trash2, RefreshCw, HardDrive, ShieldCheck,
  FileText, Archive, AlertTriangle, CheckCircle2, Clock, Server,
  Loader2, Sparkles, Layers, Info, Check, HelpCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getBackupsInfo,
  createBackup,
  downloadBackup,
  deleteBackup,
  type DatabaseStats,
  type BackupItem,
} from '../../services/BackupService';

interface BackupManagerProps {
  embedded?: boolean;
}

export function BackupManager({ embedded = false }: BackupManagerProps) {
  const [stats, setStats] = useState<DatabaseStats | null>(null);
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] = useState<BackupItem | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await getBackupsInfo();
      setStats(data.stats);
      setBackups(data.backups);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al cargar la información de copias de seguridad');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleGenerate = async (downloadImmediately: 'zip' | 'sql' | false = false) => {
    setGenerating(true);
    try {
      const res = await createBackup();
      toast.success(res.message || 'Copia de seguridad creada exitosamente');
      await loadData(true);

      if (downloadImmediately) {
        await handleDownload(res.backup.filename, downloadImmediately);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al generar la copia de seguridad');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (filename: string, format: 'zip' | 'sql') => {
    setDownloadingFile(`${filename}_${format}`);
    try {
      await downloadBackup(filename, format);
      toast.success(`Descarga de ${format.toUpperCase()} iniciada correctamente`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || `Error al descargar el archivo en formato ${format.toUpperCase()}`);
    } finally {
      setDownloadingFile(null);
    }
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;
    const filename = fileToDelete.filename;
    setDeletingFile(filename);
    try {
      await deleteBackup(filename);
      toast.success('Copia de seguridad eliminada');
      setBackups(prev => prev.filter(b => b.filename !== filename));
      setFileToDelete(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error al eliminar el respaldo');
    } finally {
      setDeletingFile(null);
    }
  };

  return (
    <div className={`space-y-6 ${embedded ? '' : 'max-w-6xl mx-auto p-4 md:p-8'}`}>
      {/* Encabezado */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2 border-b border-gray-200 dark:border-neutral-700">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-500 text-white shadow-md shadow-blue-500/20">
              <Database size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Copias de Seguridad (Backups)
                </h2>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                  <ShieldCheck size={12} />
                  Solo Admin
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Genera, almacena y descarga respaldos completos de la base de datos de DufiWaiter
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowHelp(!showHelp)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-800 transition"
            title="Instrucciones de restauración"
          >
            <HelpCircle size={16} />
            <span className="hidden sm:inline">Guía de Restauración</span>
          </button>
          <button
            type="button"
            onClick={() => loadData()}
            disabled={loading || generating}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-neutral-800 transition disabled:opacity-50"
            title="Refrescar lista"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Actualizar</span>
          </button>
        </div>
      </div>

      {/* Guía informativa de restauración (Colapsible) */}
      {showHelp && (
        <div className="rounded-xl border border-blue-200 dark:border-blue-900/60 bg-blue-50/60 dark:bg-blue-950/20 p-5 space-y-3 transition">
          <div className="flex items-center gap-2 text-blue-900 dark:text-blue-300 font-semibold text-sm">
            <Info size={18} />
            ¿Cómo restaurar una copia de seguridad?
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-blue-800 dark:text-blue-300/90 leading-relaxed">
            <div className="bg-white/70 dark:bg-neutral-800/80 p-3.5 rounded-lg border border-blue-100 dark:border-blue-900/40 space-y-1.5">
              <span className="font-bold text-gray-900 dark:text-white flex items-center gap-1.5 text-xs">
                <FileText size={14} className="text-blue-600 dark:text-blue-400" />
                Opción 1: Desde phpMyAdmin (Recomendada)
              </span>
              <ol className="list-decimal list-inside space-y-1 text-gray-600 dark:text-gray-300">
                <li>Descarga el respaldo en formato <strong>.SQL</strong> o descomprime el <strong>.ZIP</strong>.</li>
                <li>Abre phpMyAdmin y selecciona la base de datos <code className="bg-gray-100 dark:bg-neutral-700 px-1 py-0.5 rounded font-mono">{stats?.database || 'dufi'}</code>.</li>
                <li>Ve a la pestaña <strong>Importar</strong>, sube el archivo <code className="bg-gray-100 dark:bg-neutral-700 px-1 py-0.5 rounded font-mono">.sql</code> y haz clic en <strong>Continuar</strong>.</li>
              </ol>
            </div>
            <div className="bg-white/70 dark:bg-neutral-800/80 p-3.5 rounded-lg border border-blue-100 dark:border-blue-900/40 space-y-1.5">
              <span className="font-bold text-gray-900 dark:text-white flex items-center gap-1.5 text-xs">
                <Server size={14} className="text-blue-600 dark:text-blue-400" />
                Opción 2: Desde Terminal / Consola MySQL
              </span>
              <p className="text-gray-600 dark:text-gray-300">
                Ejecuta el siguiente comando en la terminal del servidor o tu PC:
              </p>
              <div className="bg-neutral-900 text-emerald-400 p-2 rounded font-mono text-[11px] overflow-x-auto">
                mysql -u root -p {stats?.database || 'u336743605_dufi'} &lt; backup.sql
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tarjetas de Estadísticas de la BD */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Base de Datos */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-gray-100 dark:border-neutral-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Base de Datos</span>
            <div className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400">
              <Database size={16} />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-lg font-bold text-gray-900 dark:text-white truncate font-mono" title={stats?.database}>
              {stats ? stats.database : '—'}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {stats ? `${stats.driver.toUpperCase()} (${stats.version.split('-')[0]})` : 'Conectada'}
              </span>
            </div>
          </div>
        </div>

        {/* Tablas Respaldadas */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-gray-100 dark:border-neutral-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Total Tablas</span>
            <div className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
              <Layers size={16} />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {stats ? stats.tables_count : '—'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Tablas del sistema Dufi
            </p>
          </div>
        </div>

        {/* Tamaño de Datos */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-gray-100 dark:border-neutral-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Tamaño Estimado</span>
            <div className="p-1.5 rounded-lg bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400">
              <HardDrive size={16} />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {stats ? `${stats.size_mb} MB` : '—'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Datos e índices indexados
            </p>
          </div>
        </div>

        {/* Copias Disponibles */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-4 border border-gray-100 dark:border-neutral-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Respaldos Guardados</span>
            <div className="p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400">
              <Archive size={16} />
            </div>
          </div>
          <div className="mt-2">
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {backups.length} {backups.length === 1 ? 'copia' : 'copias'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {backups.length > 0 ? `Último: ${backups[0].created_at_human}` : 'Sin copias registradas'}
            </p>
          </div>
        </div>
      </div>

      {/* Hero Banner de Acción Principal */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white p-6 shadow-lg border border-blue-800/40">
        {/* Decoración de fondo */}
        <div className="absolute -right-6 -bottom-6 w-48 h-48 rounded-full bg-blue-500/10 blur-2xl pointer-events-none" />
        <div className="absolute top-0 right-1/4 w-32 h-32 rounded-full bg-indigo-500/10 blur-xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-blue-200 text-xs font-semibold backdrop-blur-sm">
              <Sparkles size={14} className="text-amber-300" />
              Respaldo Integral y Seguro
            </div>
            <h3 className="text-xl md:text-2xl font-bold tracking-tight">
              Generar un Respaldo de Base de Datos
            </h3>
            <p className="text-sm text-blue-100/80 leading-relaxed">
              Exporta todos los pedidos, clientes, inventarios, configuraciones y usuarios en un archivo estructurado y limpio listo para restaurar en caso de contingencia.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            {/* Botón Principal: Generar y Guardar */}
            <button
              type="button"
              onClick={() => handleGenerate(false)}
              disabled={generating}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-500 hover:bg-blue-400 text-white font-semibold text-sm shadow-lg shadow-blue-500/25 transition disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
            >
              {generating ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Generando Copia...</span>
                </>
              ) : (
                <>
                  <Database size={18} />
                  <span>Generar Backup</span>
                </>
              )}
            </button>

            {/* Botón Secundario: Generar y Descargar SQL */}
            <button
              type="button"
              onClick={() => handleGenerate('sql')}
              disabled={generating}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/15 backdrop-blur-sm transition disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
              title="Genera un respaldo y lo descarga directamente en formato .sql"
            >
              {generating ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <FileText size={18} className="text-emerald-400" />
              )}
              <span>Generar y Descargar .SQL</span>
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Copias de Seguridad */}
      <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-gray-100 dark:border-neutral-700 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 dark:border-neutral-700 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Archive size={18} className="text-blue-600 dark:text-blue-400" />
            <h3 className="font-bold text-gray-900 dark:text-white">
              Historial de Copias de Seguridad
            </h3>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 dark:bg-neutral-700 dark:text-gray-300">
              {backups.length}
            </span>
          </div>

          <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
            Almacenadas en servidor local
          </span>
        </div>

        {loading ? (
          <div className="p-12 text-center space-y-3">
            <Loader2 size={32} className="animate-spin mx-auto text-blue-600 dark:text-blue-400" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Cargando historial de respaldos...
            </p>
          </div>
        ) : backups.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto">
              <Database size={28} />
            </div>
            <h4 className="text-base font-semibold text-gray-900 dark:text-white">
              Aún no hay copias de seguridad generadas
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              Haz clic en el botón <strong>"Generar Backup"</strong> para crear tu primera copia de seguridad de la base de datos.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50/75 dark:bg-neutral-900/50 text-gray-500 dark:text-gray-400 text-xs font-semibold uppercase tracking-wider border-b border-gray-100 dark:border-neutral-700">
                <tr>
                  <th className="px-5 py-3.5">Archivo</th>
                  <th className="px-5 py-3.5">Fecha y Hora</th>
                  <th className="px-5 py-3.5">Tamaño</th>
                  <th className="px-5 py-3.5 text-right">Acciones de Descarga</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-neutral-700/60">
                {backups.map((backup) => {
                  const isZipDownloading = downloadingFile === `${backup.filename}_zip`;
                  const isSqlDownloading = downloadingFile === `${backup.filename}_sql`;
                  const isBeingDeleted = deletingFile === backup.filename;

                  return (
                    <tr
                      key={backup.filename}
                      className="hover:bg-gray-50/80 dark:hover:bg-neutral-700/30 transition group"
                    >
                      {/* Archivo */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 shrink-0">
                            <Archive size={18} />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white font-mono text-xs md:text-sm">
                              {backup.filename}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              Paquete comprimido ZIP (.sql inside)
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Fecha y Hora */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                          <Clock size={14} className="text-gray-400" />
                          <span className="font-medium text-xs md:text-sm">
                            {new Date(backup.created_at).toLocaleString('es-CO', {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })}
                          </span>
                        </div>
                        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                          {backup.created_at_human}
                        </span>
                      </td>

                      {/* Tamaño */}
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-100 dark:bg-neutral-700 text-gray-800 dark:text-gray-200">
                          {backup.size_formatted}
                        </span>
                      </td>

                      {/* Botones de Acción */}
                      <td className="px-5 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Descargar ZIP */}
                          <button
                            type="button"
                            onClick={() => handleDownload(backup.filename, 'zip')}
                            disabled={isZipDownloading || isSqlDownloading}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 dark:hover:bg-blue-950/40 dark:hover:text-blue-300 text-xs font-medium transition disabled:opacity-50"
                            title="Descargar paquete comprimido ZIP"
                          >
                            {isZipDownloading ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <Download size={13} />
                            )}
                            <span>ZIP</span>
                          </button>

                          {/* Descargar SQL */}
                          <button
                            type="button"
                            onClick={() => handleDownload(backup.filename, 'sql')}
                            disabled={isZipDownloading || isSqlDownloading}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800/80 bg-emerald-50/50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-xs font-medium transition disabled:opacity-50"
                            title="Extraer y descargar script .SQL directamente"
                          >
                            {isSqlDownloading ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <FileText size={13} />
                            )}
                            <span>SQL Plano</span>
                          </button>

                          {/* Eliminar */}
                          <button
                            type="button"
                            onClick={() => setFileToDelete(backup)}
                            disabled={isBeingDeleted}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 dark:hover:text-red-400 transition"
                            title="Eliminar este respaldo"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de confirmación para eliminar */}
      {fileToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-neutral-800 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 dark:border-neutral-700 space-y-4">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto">
              <AlertTriangle size={24} />
            </div>

            <div className="text-center space-y-1.5">
              <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                ¿Eliminar copia de seguridad?
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Esta acción eliminará permanentemente el archivo{' '}
                <code className="bg-gray-100 dark:bg-neutral-700 px-1 py-0.5 rounded font-mono font-bold text-gray-800 dark:text-gray-200">
                  {fileToDelete.filename}
                </code>{' '}
                del servidor. No podrás recuperarlo a menos que lo hayas descargado previamente.
              </p>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setFileToDelete(null)}
                disabled={deletingFile !== null}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-neutral-700 text-gray-700 dark:text-gray-300 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-neutral-700 transition"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deletingFile !== null}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm shadow-md shadow-red-500/20 transition disabled:opacity-50"
              >
                {deletingFile !== null ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
                <span>Eliminar Definitivamente</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
